import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { useLocalize, useViewer } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import { GraphQLErrorMessage, Jumbo, Paragraph, QuillText } from '@treams/treams-ui';

import { growthPathDataMappingFromJobTitle } from '../GrowthPathConverter';
import GrowthPathDetailBody, {
	GrowthPathDetailBodySkeleton,
	GrowthPathEmpty,
} from '../components/GrowthPathDetailBody';
import GrowthPathHeader from '../components/GrowthPathHeader';
import LabelQueryWrapper from '../components/LabelQueryWrapper';
import { useGrowthPathContext } from '../contexts/GrowthPathContext';
import { GrowthPathDetailContainer } from '../pages/GrowthPath';

const JobTitleDetail: FC = () => {
	const { t } = useTranslation();
	const viewer = useViewer();
	const { localize } = useLocalize();
	const { userID, jobTitleID } = useGrowthPathContext();

	const { data, loading, error } = graphql.useJobTitleCompetenciesQuery({
		variables: { id: jobTitleID, userID: userID ?? viewer!.id },
		skip: !jobTitleID,
		fetchPolicy: 'cache-and-network',
	});

	const jobTitle = data?.jobTitle;
	const jobCompetencies = data?.jobTitle.competencies ?? [];

	const hasDescription =
		jobTitle?.description?.[0]?.text?.ops?.length &&
		jobTitle?.description?.[0]?.text?.ops?.length > 0;

	if (loading) {
		return <JobTitleDetailSkeleton />;
	}
	if (error) {
		return <GraphQLErrorMessage error={error.graphQLErrors[0]} />;
	}

	return (
		<GrowthPathDetailContainer>
			<GrowthPathHeader title={jobTitle ? localize(jobTitle.title).text : ''}>
				{hasDescription && jobTitle.description && (
					<Paragraph py={2}>
						<QuillText value={localize(jobTitle.description).text} />
					</Paragraph>
				)}
			</GrowthPathHeader>

			{jobTitle && jobTitle.competencies && jobTitle.competencies.length > 0 ? (
				<LabelQueryWrapper jobCompetencies={jobCompetencies} data={data}>
					<GrowthPathDetailBody data={growthPathDataMappingFromJobTitle(jobTitle)} />
				</LabelQueryWrapper>
			) : (
				<GrowthPathEmpty
					title={t('my.jobTitles.detail.empty.title')}
					description={t('my.jobTitles.detail.empty.description')}
				/>
			)}
		</GrowthPathDetailContainer>
	);
};

const JobTitleDetailSkeleton = () => {
	return (
		<>
			<Jumbo.Skeleton wide />
			<GrowthPathDetailBodySkeleton />
		</>
	);
};

export default JobTitleDetail;
